import { useMemo, useRef, useState } from 'react'
import './AmigoWalkthroughVideo.css'

type FrameCandidate = { file: File; url: string; score: number; time: number }
type Props = { onFrames: (files: File[]) => void }

function mediaErrorMessage(video: HTMLVideoElement, file: File) {
  const code = video.error?.code
  if (code === MediaError.MEDIA_ERR_DECODE || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
    const isAppleVideo = file.type === 'video/quicktime' || /\.mov$/i.test(file.name)
    return isAppleVideo
      ? 'Safari could not extract frames from this iPhone video. You can continue immediately with three guided photos, or record a new video using Most Compatible in iPhone Camera settings.'
      : 'This video format could not be decoded on this device. Continue with three guided photos or record again using MP4/H.264.'
  }
  return 'This video could not be opened. Continue with three guided photos or record again and keep this page open while it processes.'
}

async function waitForVideo(video: HTMLVideoElement, file: File) {
  await new Promise<void>((resolve, reject) => {
    let settled = false
    const timeout = window.setTimeout(() => fail(new Error('The video took too long to load. Record a shorter walkthrough and try again.')), 15000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('loadedmetadata', ready)
      video.removeEventListener('loadeddata', ready)
      video.removeEventListener('canplay', ready)
      video.removeEventListener('error', onError)
    }
    const ready = () => {
      if (settled || !Number.isFinite(video.duration) || video.videoWidth < 1 || video.videoHeight < 1) return
      settled = true
      cleanup()
      resolve()
    }
    const fail = (error: Error) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }
    const onError = () => fail(new Error(mediaErrorMessage(video, file)))
    video.addEventListener('loadedmetadata', ready)
    video.addEventListener('loadeddata', ready)
    video.addEventListener('canplay', ready)
    video.addEventListener('error', onError)
    video.load()
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) ready()
  })
}

async function seek(video: HTMLVideoElement, time: number) {
  await new Promise<void>((resolve, reject) => {
    let settled = false
    const timeout = window.setTimeout(() => fail(new Error('The video could not be scanned at this point.')), 8000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('seeked', done)
      video.removeEventListener('error', failed)
    }
    const done = () => {
      if (settled) return
      settled = true
      cleanup()
      resolve()
    }
    const fail = (error: Error) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }
    const failed = () => fail(new Error('Unable to read the video.'))
    video.addEventListener('seeked', done, { once: true })
    video.addEventListener('error', failed, { once: true })
    const safeTime = Math.min(Math.max(.05, time), Math.max(.05, video.duration - .05))
    if (typeof video.fastSeek === 'function') video.fastSeek(safeTime)
    else video.currentTime = safeTime
  })
}

function frameScore(context: CanvasRenderingContext2D, width: number, height: number) {
  const data = context.getImageData(0, 0, width, height).data
  let brightness = 0
  const values: number[] = []
  for (let index = 0; index < data.length; index += 4) {
    const value = 0.2126 * data[index]! + 0.7152 * data[index + 1]! + 0.0722 * data[index + 2]!
    values.push(value)
    brightness += value
  }
  brightness /= values.length
  let contrast = 0
  for (const value of values) contrast += Math.abs(value - brightness)
  contrast /= values.length
  const exposurePenalty = brightness < 45 || brightness > 225 ? 45 : Math.abs(brightness - 135) * 0.08
  return contrast - exposurePenalty
}

async function extractFrames(file: File): Promise<FrameCandidate[]> {
  if (!file.type.startsWith('video/') && !/\.(mov|mp4|m4v|webm)$/i.test(file.name)) throw new Error('Choose a valid video file.')
  if (file.size > 250 * 1024 * 1024) throw new Error('Keep the walkthrough under 250 MB. Record a shorter video and try again.')

  const videoUrl = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.preload = 'auto'
  video.muted = true
  video.defaultMuted = true
  video.playsInline = true
  video.setAttribute('playsinline', '')
  video.setAttribute('webkit-playsinline', '')
  video.style.position = 'fixed'
  video.style.width = '1px'
  video.style.height = '1px'
  video.style.opacity = '0'
  video.style.pointerEvents = 'none'
  video.src = videoUrl
  document.body.appendChild(video)

  try {
    await waitForVideo(video, file)
    if (!Number.isFinite(video.duration) || video.duration < 4) throw new Error('Record at least 4 seconds of the room.')
    if (video.duration > 60) throw new Error('Keep the walkthrough under 60 seconds.')

    const sampleCount = Math.min(12, Math.max(6, Math.round(video.duration / 2)))
    const times = Array.from({ length: sampleCount }, (_, index) => Math.max(.2, ((index + 1) / (sampleCount + 1)) * video.duration))
    const canvas = document.createElement('canvas')
    const maxWidth = 1600
    const scale = Math.min(1, maxWidth / Math.max(1, video.videoWidth))
    canvas.width = Math.max(640, Math.round(video.videoWidth * scale))
    canvas.height = Math.max(360, Math.round(video.videoHeight * scale))
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) throw new Error('This browser could not process the walkthrough.')

    const candidates: FrameCandidate[] = []
    for (const time of times) {
      await seek(video, time)
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      const scoreCanvas = document.createElement('canvas')
      scoreCanvas.width = 96
      scoreCanvas.height = 72
      const scoreContext = scoreCanvas.getContext('2d', { willReadFrequently: true })
      if (!scoreContext) continue
      scoreContext.drawImage(canvas, 0, 0, 96, 72)
      const score = frameScore(scoreContext, 96, 72)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', .88))
      if (!blob) continue
      const frame = new File([blob], `walkthrough-${Math.round(time * 10)}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
      candidates.push({ file: frame, url: URL.createObjectURL(frame), score, time })
    }
    return candidates.sort((a, b) => b.score - a.score)
  } finally {
    video.pause()
    video.removeAttribute('src')
    video.load()
    video.remove()
    URL.revokeObjectURL(videoUrl)
  }
}

export function AmigoWalkthroughVideo({ onFrames }: Props) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [frames, setFrames] = useState<FrameCandidate[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const selected = useMemo(() => {
    if (frames.length <= 3) return frames
    const sortedByTime = [...frames.slice(0, 8)].sort((a, b) => a.time - b.time)
    return [sortedByTime[0], sortedByTime[Math.floor(sortedByTime.length / 2)], sortedByTime[sortedByTime.length - 1]].filter((item): item is FrameCandidate => Boolean(item))
  }, [frames])

  async function processVideo(file: File) {
    setIsProcessing(true)
    setError(null)
    frames.forEach((frame) => URL.revokeObjectURL(frame.url))
    setFrames([])
    try {
      const next = await extractFrames(file)
      if (next.length < 3) throw new Error('AMIGO could not find three clear views. Record again more slowly with better lighting.')
      setFrames(next)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to process this walkthrough.')
    } finally {
      setIsProcessing(false)
    }
  }

  function useFrames() {
    onFrames(selected.map((frame) => frame.file))
  }

  function continueWithGuidedPhotos() {
    setError(null)
    const scanButton = document.querySelector<HTMLButtonElement>('.camera-scan-summary .secondary-button')
    scanButton?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    window.setTimeout(() => scanButton?.click(), 350)
  }

  return <section className="walkthrough-video-card">
    <div className="walkthrough-heading"><span>🎥</span><div><strong>Walkthrough Video</strong><p>Record a slow 5–30 second walkthrough. AMIGO selects three useful frames locally on your device.</p></div></div>
    <ol className="walkthrough-tips"><li>Start at one corner.</li><li>Pan slowly across the room.</li><li>Finish facing the opposite side.</li></ol>
    <input ref={inputRef} className="camera-scan-input" type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/*" capture="environment" onChange={(event) => { const file = event.target.files?.[0]; if (file) void processVideo(file); event.currentTarget.value = '' }} />
    <button type="button" className="secondary-button" disabled={isProcessing} onClick={() => inputRef.current?.click()}>{isProcessing ? 'Selecting the best frames…' : frames.length ? 'Record another walkthrough' : 'Record walkthrough video'}</button>
    {error && <div className="camera-quality-card poor"><strong>Video could not be processed</strong><span>{error}</span><button type="button" className="secondary-button" onClick={continueWithGuidedPhotos}>Continue with 3 guided photos</button></div>}
    {selected.length === 3 && <><div className="walkthrough-frames">{selected.map((frame, index) => <figure key={frame.url}><img src={frame.url} alt={`Selected walkthrough frame ${index + 1}`} /><figcaption>View {index + 1} · {frame.time.toFixed(1)}s</figcaption></figure>)}</div><button type="button" className="camera-capture-button" onClick={useFrames}>Use these 3 frames</button></>}
    <div className="depth-readiness"><span>⌁</span><div><strong>Depth/LiDAR ready architecture</strong><p>Future native scans can replace these visual frames with calibrated depth maps while keeping the same estimate workflow.</p></div><small>Native phase</small></div>
  </section>
}
